import axios from "axios";

const API_URL = import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8050";

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json"
  }
});

export type BillingPeriod = "month" | "year";

export type SeamlessChargeStatus = "succeeded" | "failed" | "confirmation_required" | "processing";

export interface SeamlessChargeResponse {
  status: SeamlessChargeStatus;
  paymentId: string;
  amount: number;
  minutes?: number;
  plan?: string;
  billingPeriod?: BillingPeriod;
  failureReason?: string | null;
  failureMessage?: string | null;
  confirmationUrl?: string;
}

export interface PaymentVerifyResponse {
  id: string;
  type: string;
  amount: number;
  status: string;
  minutesPurchased: number | null;
  createdAt: string;
  plan: string | null;
  billingPeriod: BillingPeriod | null;
  failureReason?: string | null;
  failureMessage?: string | null;
}

export interface BalanceResponse {
  plan: "free" | "trial" | "basic" | "pro";
  planName: string;
  trialEndDate?: string | null;
  trialMinutes: number;
  subscriptionMinutes: number;
  extraMinutes: number;
  totalMinutes: number;
  reservedMinutes: number; // НОВОЕ: минуты в активных резервациях
  availableMinutes: number; // НОВОЕ: доступно для новых транскрипций
  remainingSummaries: number;
  includedSummaries: number;
  extraMinutePrice: number;
  renewDate: string | null;
  autoRenew: boolean; // Статус автопродления
  billingStatus: "active" | "payment_failed" | "none"; // Состояние рекуррентного списания
  retryAttempt: number; // Сколько попыток списания уже сделано
  maxRetryAttempts: number; // Всего попыток в цикле
  nextChargeDate: string | null; // Когда следующая попытка списания
  graceUntil: string | null; // До какой даты сохранён доступ, пока идут попытки
  currentPrice: number | null; // Цена следующего списания
  isLegacyPrice: boolean; // Цена зафиксирована ниже актуального прайса
  previewEligible: boolean; // Положено ли бесплатное превью
  previewsLeft: number; // Сколько превью осталось
  previewLimitSeconds: number; // Длительность превью в секундах
  billingPeriod: BillingPeriod; // Период оплаты текущей подписки
  nextMinutesRefillAt: string | null; // Когда пополнятся минуты на годовом тарифе
  pendingPlan: string | null; // Запланированный переход: тариф
  pendingBillingPeriod: BillingPeriod | null; // Запланированный переход: период
  yearlyPrice: number | null; // Годовая цена текущего тарифа, если применимо
}

export interface BuySubscriptionRequest {
  plan: "basic" | "pro";
}

export interface BuyMinutesRequest {
  minutes: number;
}

export interface BuyMinutesResponse {
  success: boolean;
  minutesPurchased: number;
  cost: number;
  message: string;
}

export interface BuySubscriptionResponse {
  success: boolean;
  message: string;
}

export interface PaymentResponse {
  paymentId: string;
  paymentUrl: string;
  amount: number;
  currency: string;
  invoiceId: string;
}

export interface CancelSubscriptionResponse {
  success: boolean;
  message: string;
}

export interface SubscriptionQuoteResponse {
  fullPrice: number;
  credit: number;
  amount: number;
  action: "upgrade" | "downgrade" | "same";
  effectiveDate: string;
}

export interface ScheduleSubscriptionChangeResponse {
  scheduled: boolean;
  effectiveDate: string;
  plan: string;
  billingPeriod: BillingPeriod;
}

export interface CancelScheduledChangeResponse {
  cancelled: boolean;
}

export type LinkedPaymentMethodType = "card" | "sberpay" | "sbp" | "tpay" | "yoomoney" | "unknown";

export interface LinkedCardResponse {
  hasCard: boolean;
  type?: LinkedPaymentMethodType;
  last4?: string;
  brand?: string;
}

export interface UnlinkCardResponse {
  success: boolean;
  message: string;
}

export interface PaymentHistoryItem {
  id: string;
  type: "subscription" | "extra_minutes";
  amount: number;
  minutesPurchased: number | null;
  status: "pending" | "completed" | "failed" | "refunded";
  createdAt: string;
  updatedAt: string;
  billingPeriod?: BillingPeriod | null;
}

export interface PaginatedPaymentHistory {
  items: PaymentHistoryItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const subscriptionApi = {
  getBalance: async (): Promise<BalanceResponse> => {
    const response = await api.get<BalanceResponse>("/subscription/balance");
    return response.data;
  },

  buySubscription: async (plan: BuySubscriptionRequest["plan"]): Promise<BuySubscriptionResponse> => {
    const response = await api.post<BuySubscriptionResponse>("/subscription/buy", { plan });
    return response.data;
  },

  buyMinutes: async (minutes: number): Promise<BuyMinutesResponse> => {
    const response = await api.post<BuyMinutesResponse>("/subscription/buy-minutes", { minutes });
    return response.data;
  },

  createSubscriptionPayment: async (
    plan: BuySubscriptionRequest["plan"],
    billingPeriod: BillingPeriod = "month"
  ): Promise<PaymentResponse> => {
    const response = await api.post<PaymentResponse>("/payment/yookassa/subscription", { plan, billingPeriod });
    return response.data;
  },

  getSubscriptionQuote: async (
    plan: BuySubscriptionRequest["plan"],
    billingPeriod: BillingPeriod
  ): Promise<SubscriptionQuoteResponse> => {
    const response = await api.get<SubscriptionQuoteResponse>("/payment/yookassa/subscription/quote", {
      params: { plan, billingPeriod }
    });
    return response.data;
  },

  scheduleSubscriptionChange: async (
    plan: BuySubscriptionRequest["plan"],
    billingPeriod: BillingPeriod
  ): Promise<ScheduleSubscriptionChangeResponse> => {
    const response = await api.post<ScheduleSubscriptionChangeResponse>(
      "/payment/yookassa/subscription/schedule-change",
      {
        plan,
        billingPeriod
      }
    );
    return response.data;
  },

  cancelScheduledChange: async (): Promise<CancelScheduledChangeResponse> => {
    const response = await api.delete<CancelScheduledChangeResponse>("/payment/yookassa/subscription/scheduled-change");
    return response.data;
  },

  createMinutesPayment: async (minutes: number): Promise<PaymentResponse> => {
    const response = await api.post<PaymentResponse>("/payment/yookassa/minutes", { minutes });
    return response.data;
  },

  createTrialPayment: async (): Promise<PaymentResponse> => {
    const response = await api.post<PaymentResponse>("/payment/yookassa/trial");
    return response.data;
  },

  cancelSubscription: async (): Promise<CancelSubscriptionResponse> => {
    const response = await api.post<CancelSubscriptionResponse>("/subscription/cancel");
    return response.data;
  },

  getPaymentHistory: async (page: number = 1, limit: number = 10): Promise<PaginatedPaymentHistory> => {
    const response = await api.get<PaginatedPaymentHistory>("/payment/history", {
      params: { page, limit }
    });
    return response.data;
  },

  getLinkedCard: async (): Promise<LinkedCardResponse> => {
    const response = await api.get<LinkedCardResponse>("/payment/yookassa/card");
    return response.data;
  },

  unlinkCard: async (): Promise<UnlinkCardResponse> => {
    const response = await api.delete<UnlinkCardResponse>("/payment/yookassa/card");
    return response.data;
  },

  chargeMinutesWithSavedCard: async (minutes: number): Promise<SeamlessChargeResponse> => {
    const response = await api.post<SeamlessChargeResponse>("/payment/yookassa/minutes/charge", { minutes });
    return response.data;
  },

  chargeSubscriptionWithSavedCard: async (
    plan: string,
    billingPeriod: BillingPeriod = "month"
  ): Promise<SeamlessChargeResponse> => {
    const response = await api.post<SeamlessChargeResponse>("/payment/yookassa/subscription/charge", {
      plan,
      billingPeriod
    });
    return response.data;
  },

  verifyPayment: async (paymentId: string): Promise<PaymentVerifyResponse> => {
    const response = await api.get<PaymentVerifyResponse>(`/payment/${paymentId}/verify`);
    return response.data;
  }
};
